import {
    checkCartValidation,
    removeValidationStatus,
    ValidateCartApiParameter,
} from "@iewc/Services/CustomCartValidationService";
import setCustomShippingDetails from "@iewc/Store/Handlers/CheckoutReviewAndSubmit/CustomSetShippingDetails";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { addToChainAfter, makeHandlerChainAwaitable, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { Cart } from "@insite/client-framework/Services/CartService";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    chain,
    GetCart,
    PopulateApiParameter,
    SetNeedFullCart,
} from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { CartLineModel, RealTimePricingModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";

replaceInChain(chain, SetNeedFullCart, props => {
    const pageType = getCurrentPage(props.getState()).type;

    props.needFullCart =
        props.parameter.shouldLoadFullCart ||
        (props.parameter.shouldLoadFullCart !== false &&
            (pageType === "CheckoutShippingPage" ||
                pageType === "CheckoutReviewAndSubmitPage" ||
                pageType === "CartPage" ||
                pageType === "OnePageCheckoutPage" ||
                pageType === "RfqRequestQuotePage" ||
                pageType === "RfqRequestQuoteGuestPage")); // Verndale Customization RfqRequestQuoteGuestPage
});

replaceInChain(chain, PopulateApiParameter, props => {
    const pageType = getCurrentPage(props.getState()).type;
    const currentTimestamp = Date.now();
    props.apiParameter = {
        cartId: API_URL_CURRENT_FRAGMENT,
        expand: ["shipping", "cartLines"],
        additionalQueryStringParameters: {
            isSpire: true,
            timeStamp: currentTimestamp,
        },
    };

    if (!getSession(props.getState()).rememberMe) {
        props.apiParameter.expand?.push("validation");
    }

    if (props.needFullCart) {
        props.apiParameter.forceRecalculation = true;
        props.apiParameter.allowInvalidAddress = true;
        props.apiParameter.expand = [
            ...(props.apiParameter.expand || []),
            "cartLines",
            "restrictions",
            "tax",
            "carriers",
            "paymentOptions",
            "costCodes",
            `${currentTimestamp}`,
        ] as any[];

        if (pageType === "CartPage") {
            props.apiParameter.expand.push("hiddenproducts");
        }

        if (pageType === "CheckoutReviewAndSubmitPage" || pageType === "OrderConfirmationPage") {
            props.apiParameter.expand.push("orderables" as any);
        }
    }

    if (props.parameter.getCartLines) {
        props.apiParameter.expand?.push("cartLines");
    }

    if (props.parameter.getCostCodes) {
        props.apiParameter.expand?.push("costCodes");
    }
});

addToChainAfter(chain, GetCart, async props => {
    const pageType = getCurrentPage(props.getState()).type;
    if (pageType === "CartPage") {
        if ((props.apiResult.cart.cartLines ?? []).length === 0) {
            return;
        }

        const loadRealTimePricingParameter: Parameters<typeof loadRealTimePricing>[0] = {
            productPriceParameters:
                props.apiResult.cart.cartLines?.map(o => ({
                    productId: o.productId ?? "",
                    unitOfMeasure: o.unitOfMeasure ?? "",
                    qtyOrdered: o.qtyOrdered ?? 0,
                    properties: {
                        spoolLength: o.properties?.spoolLength ?? "",
                        spoolSelected: o.properties?.spoolSelected ?? "",
                    },
                })) ?? [],
            onSuccess: realTimePricing => {
                (props as any).pricingLoaded = true;

                props.apiResult.cart.cartLines?.forEach(cartLine => {
                    const result = realTimePricing.realTimePricingResults?.find(
                        p => p.productId === cartLine.productId,
                    );
                    if (result) {
                        cartLine.pricing = result;
                    }
                });
            },
            onError: () => {
                (props as any).pricingLoaded = true;
            },
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    this.onSuccess?.(realTimePricingProps.apiResult);
                } else if (realTimePricingProps.error) {
                    this.onError?.(realTimePricingProps.error);
                }
            },
        };

        const settings = getSettingsCollection(props.getState());

        if (IS_SERVER_SIDE && getIsWebCrawler() && !settings.websiteSettings.includeRealTimeInventoryAndPricingForSeo) {
            const awaitableLoadRealTimePricing = makeHandlerChainAwaitable<
                Parameters<typeof loadRealTimePricing>[0],
                RealTimePricingModel
            >(loadRealTimePricing);
            const realTimePricing = await awaitableLoadRealTimePricing({
                productPriceParameters: loadRealTimePricingParameter.productPriceParameters,
            })(props.dispatch, props.getState);
            if (realTimePricing) {
                loadRealTimePricingParameter.onSuccess?.(realTimePricing);
            } else {
                loadRealTimePricingParameter.onError?.(realTimePricing);
            }
        } else {
            props.dispatch(loadRealTimePricing(loadRealTimePricingParameter));
        }

        await waitFor(() => !!(props as any).pricingLoaded);
    }
});

addToChainAfter(chain, GetCart, async props => {
    const pageType = getCurrentPage(props.getState()).type;
    if ((props.apiResult.cart.cartLines ?? []).length === 0) {
        return;
    }

    if (pageType === "CartPage") {
        const cartApiParameter = buildCartApiParameter(props.apiResult.cart, props.apiResult.shipTo);
        await removeValidationStatus(cartApiParameter);
    }
    if (pageType === "CheckoutShippingPage") {
        const cartLines =
            props.apiResult.cart.cartLines?.map((cartLine: CartLineModel) => ({
                id: cartLine.id,
                estimateShipDate: cartLine.properties?.calendarShippingDate,
            })) ?? [];

        props.dispatch(
            setCustomShippingDetails({
                onCalendar:
                    (props.apiResult.shipTo?.properties &&
                        props.apiResult.shipTo?.properties["onCalendar"]?.toLowerCase() === "y") ??
                    false,
                cartEstimateShipDate: props.apiResult.cart.properties["onCalendarShippingDate"],
                cartLines,
            }),
        );
    }
    if (pageType === "CheckoutReviewAndSubmitPage") {
        await checkCartValidation();
    }
});

const buildCartApiParameter = (cart?: Cart, shipTo?: ShipToModel): ValidateCartApiParameter => {
    const cartToValidate: ValidateCartApiParameter = {
        lines: [],
    };

    if (cart) {
        cartToValidate.orderNumber = cart.orderNumber ?? "";
    }

    if (shipTo) {
        cartToValidate.shipToCustNbr = shipTo.customerSequence ?? "";
        cartToValidate.shipToRegion = shipTo.state?.abbreviation ?? "";
    }

    cart?.cartLines?.forEach(line => {
        cartToValidate.lines.push({
            ProductNbr: line.erpNumber ?? "",
            CartQuantity: line.qtyOrdered?.toString() ?? "",
            QtyUom: line.unitOfMeasure ?? "",
            LooseEnds: line.properties?.looseEnds ?? "No",
            CartlineId: line.id ?? "",
            IsQuote: line.properties?.isQuote ?? false,
        });
    });

    return cartToValidate;
};
